/**
 * Utility for downloading files to workspace folder instead of browser Downloads
 * 
 * Benefits:
 * - Files saved to workspace folder (accessible to AI/tools)
 * - Organized in dedicated Downloads_from_backtesting_frontend directory
 * - Automatic timestamping for version control
 * - Consistent file naming
 * 
 * NOTE: This module requires callAction to be passed in, it doesn't use api directly
 */

const WORKSPACE_DOWNLOAD_DIR = 'Downloads_from_backtesting_frontend';

/**
 * Download JSON data to workspace folder using backtesting endpoint
 * 
 * @param {Function} callAction - The callAction function from backtesting
 * @param {Object} data - Data to save as JSON
 * @param {string} filename - Filename (without extension)
 * @param {boolean} addTimestamp - Whether to add timestamp to filename (default: true)
 * @returns {Promise<Object>} Response with success status and file path
 */
export const downloadJSONToWorkspace = async (callAction, data, filename, addTimestamp = true) => {
  try {
    // Add timestamp to filename if requested
    const timestamp = addTimestamp ? `_${new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)}` : '';
    const fullFilename = `${filename}${timestamp}.json`;
    
    // Convert data to JSON string
    const jsonString = JSON.stringify(data, null, 2);
    
    // Call backtesting endpoint with save_to_workspace action
    const response = await callAction('save_to_workspace', {
      directory: WORKSPACE_DOWNLOAD_DIR,
      filename: fullFilename,
      content: jsonString,
      content_type: 'json'
    });
    
    if (response?.success) {
      return {
        success: true,
        filepath: response.data?.filepath || `${WORKSPACE_DOWNLOAD_DIR}/${fullFilename}`,
        message: `Saved to ${response.data?.filepath || fullFilename}`
      };
    } else {
      throw new Error(response?.error || 'Failed to save file');
    }
  } catch (error) {
    console.error('Error saving to workspace:', error);
    return {
      success: false,
      error: error.message || 'Failed to save file to workspace'
    };
  }
};

/**
 * Download CSV data to workspace folder using backtesting endpoint
 * 
 * @param {Function} callAction - The callAction function from backtesting
 * @param {string} csvContent - CSV content as string
 * @param {string} filename - Filename (without extension)
 * @param {boolean} addTimestamp - Whether to add timestamp to filename (default: true)
 * @returns {Promise<Object>} Response with success status and file path
 */
export const downloadCSVToWorkspace = async (callAction, csvContent, filename, addTimestamp = true) => {
  try {
    const timestamp = addTimestamp ? `_${new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)}` : '';
    const fullFilename = `${filename}${timestamp}.csv`;
    
    const response = await callAction('save_to_workspace', {
      directory: WORKSPACE_DOWNLOAD_DIR,
      filename: fullFilename,
      content: csvContent,
      content_type: 'csv'
    });
    
    if (response?.success) {
      return {
        success: true,
        filepath: response.data?.filepath || `${WORKSPACE_DOWNLOAD_DIR}/${fullFilename}`,
        message: `Saved to ${response.data?.filepath || fullFilename}`
      };
    } else {
      throw new Error(response?.error || 'Failed to save file');
    }
  } catch (error) {
    console.error('Error saving to workspace:', error);
    return {
      success: false,
      error: error.message || 'Failed to save file to workspace'
    };
  }
};

/**
 * Fallback: Download to browser's Downloads folder (original behavior)
 * Use this if workspace download fails
 * 
 * @param {Object} data - Data to download
 * @param {string} filename - Full filename with extension
 * @param {string} mimeType - MIME type (default: application/json)
 */
export const downloadToBrowser = (data, filename, mimeType = 'application/json') => {
  const dataStr = typeof data === 'string' 
    ? data 
    : `data:${mimeType};charset=utf-8,${encodeURIComponent(JSON.stringify(data, null, 2))}`;
  
  const downloadAnchorNode = document.createElement('a');
  downloadAnchorNode.setAttribute('href', dataStr.startsWith('data:') ? dataStr : `data:${mimeType};charset=utf-8,${encodeURIComponent(dataStr)}`);
  downloadAnchorNode.setAttribute('download', filename);
  document.body.appendChild(downloadAnchorNode);
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
};

/**
 * Smart download: Try workspace first, fallback to browser
 * 
 * @param {Function} callAction - The callAction function from backtesting
 * @param {Object} data - Data to download
 * @param {string} filename - Filename (without extension for workspace, with extension for browser)
 * @param {Object} options - Options { type: 'json'|'csv', addTimestamp: boolean, showMessage: function }
 */
export const smartDownload = async (callAction, data, filename, options = {}) => {
  const { 
    type = 'json', 
    addTimestamp = true, 
    showMessage = null 
  } = options;
  
  // Remove extension from filename for workspace download
  const baseFilename = filename.replace(/\.(json|csv)$/i, '');
  
  try {
    let result;
    if (type === 'json') {
      result = await downloadJSONToWorkspace(callAction, data, baseFilename, addTimestamp);
    } else if (type === 'csv') {
      result = await downloadCSVToWorkspace(callAction, data, baseFilename, addTimestamp);
    }
    
    if (result.success) {
      if (showMessage) {
        showMessage.success(result.message);
      }
      return result;
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    console.warn('Workspace download failed, falling back to browser download:', error);
    
    // Fallback to browser download
    const fullFilename = addTimestamp 
      ? `${baseFilename}_${new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)}.${type}`
      : `${baseFilename}.${type}`;
    
    downloadToBrowser(data, fullFilename, type === 'json' ? 'application/json' : 'text/csv');
    
    if (showMessage) {
      showMessage.info(`Downloaded to browser Downloads folder: ${fullFilename}`);
    }
    
    return {
      success: true,
      fallback: true,
      filename: fullFilename
    };
  }
};

export default {
  downloadJSONToWorkspace,
  downloadCSVToWorkspace,
  downloadToBrowser,
  smartDownload
};

